import type { AxiosInstance } from 'axios'

import {
  parseWorkspacePreference,
  parseWorkspacePreferenceList,
  parseWorkspacePreferenceWrite,
  type WorkspacePreferenceEntry,
} from '../models/workspacePreference.wire'

import { appHttp, decodeDto, unwrap } from '@/shared/http'

export interface WorkspacePreferenceScope {
  accountId: number
  workspaceId: number | string
}

/** Generic workspace-preference transport. Feature keys and value DTOs belong to consumers. */
export class WorkspacePreferencesApi {
  constructor(private readonly http: Pick<AxiosInstance, 'get' | 'post'> = appHttp) {}

  private config(signal?: AbortSignal) {
    return {
      ...(signal ? { signal } : {}),
      suppressGlobalErrorHandler: true,
      meta: { suppressGlobalErrorHandler: true },
    }
  }

  async list(
    scope: WorkspacePreferenceScope,
    signal?: AbortSignal,
  ): Promise<WorkspacePreferenceEntry[]> {
    const body = unwrap(
      await this.http.get<unknown>(
        `/accounts/${scope.accountId}/workspace/preferences/${scope.workspaceId}/`,
        this.config(signal),
      ),
    )
    return decodeDto(body, parseWorkspacePreferenceList)
  }

  async get<T>(
    scope: WorkspacePreferenceScope,
    key: string,
    signal?: AbortSignal,
  ): Promise<WorkspacePreferenceEntry<T> | null> {
    const body = unwrap(
      await this.http.get<unknown>(
        `/accounts/${scope.accountId}/workspace/preferences/${scope.workspaceId}/get/`,
        { ...this.config(signal), params: { key } },
      ),
    )
    return decodeDto(body, (value) =>
      parseWorkspacePreference(value, key),
    ) as WorkspacePreferenceEntry<T> | null
  }

  async upsert<T>(
    scope: WorkspacePreferenceScope,
    key: string,
    value: T,
    signal?: AbortSignal,
  ): Promise<void> {
    const body = unwrap(
      await this.http.post<unknown>(
        `/accounts/${scope.accountId}/workspace/preferences/${scope.workspaceId}/create/?upsert=true`,
        { key, value },
        this.config(signal),
      ),
    )
    decodeDto(body, parseWorkspacePreferenceWrite)
  }
}

export const workspacePreferencesApi = new WorkspacePreferencesApi()
